import { useEffect, useState } from "react";
import { FiEdit2, FiTrash2, FiPlus } from "react-icons/fi";
import { IoIosSearch } from "react-icons/io";
import { Sprout } from "lucide-react";
import axios from "axios";

import {
  confirmAction,
  showError,
  showSuccess,
} from "../../../utils/toastUtils";
import { ModalWrapper } from "../../../components/AdminSideComponents/common/ModalWrapper";
import api from "../../../configs/api";
const EMPTY_FORM = { plantName: "", gradeMixtures: [] };

const PlantsTab = () => {
  const [plants, setPlants] = useState([]);
  const [allMixtures, setAllMixtures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlant, setEditingPlant] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [plRes, mRes] = await Promise.all([
        api.get(`/plants`),
        api.get(`/grade-mixtures`),
      ]);

      console.log("Plants response:", plRes?.data?.data);
      console.log(
        "First plant gradeMixtures:",
        plRes?.data?.data[0]?.gradeMixtures,
      );

      setPlants(plRes?.data?.data);
      setAllMixtures(mRes?.data?.data);
    } catch {
      showError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openAdd = () => {
    setEditingPlant(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = (plant) => {
    setEditingPlant(plant);
    setForm({
      plantName: plant.plantName,
      gradeMixtures: plant.gradeMixtures.map((m) => m._id || m),
    });
    setModalOpen(true);
  };

  const toggleMixture = (id) => {
    setForm((prev) => ({
      ...prev,
      gradeMixtures: prev.gradeMixtures.includes(id)
        ? prev.gradeMixtures.filter((m) => m !== id)
        : [...prev.gradeMixtures, id],
    }));
  };

  const handleSave = async () => {
    if (!form.plantName.trim()) return showError("Plant name is required");
    try {
      setSaving(true);
      if (editingPlant) {
        await api.put(`/plants/${editingPlant._id}`, form);
        showSuccess("Plant updated");
      } else {
        await api.post(`/plants`, form);
        showSuccess("Plant created");
      }
      await fetchData();
      setModalOpen(false);
    } catch {
      showError("Failed to save plant");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (plant) => {
    const confirmed = await confirmAction({
      title: "Delete Plant?",
      text: "This action cannot be undone.",
    });
    if (!confirmed) return;
    try {
      await api.delete(`/plants/${plant._id}`);
      setPlants((prev) => prev.filter((p) => p._id !== plant._id));
      showSuccess("Plant deleted");
    } catch {
      showError("Failed to delete plant");
    }
  };

  const filtered = plants.filter((p) =>
    p.plantName.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div>
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-descriptionText">
            <IoIosSearch className="size-4" />
          </span>
          <input
            type="text"
            placeholder="Search plants..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-[42px] w-full sm:w-[25rem] pl-9 pr-4 border border-borderLight outline-none focus:border-primaryAccent text-sm"
          />
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2 bg-primaryAccent text-white text-sm h-[42px]"
        >
          <FiPlus /> Add Plant
        </button>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {loading ? (
          Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="border border-borderLight bg-surfaceColor p-5 animate-pulse space-y-3"
            >
              <div className="h-3 bg-divider w-1/2" />
              <div className="h-3 bg-divider w-3/4" />
              <div className="h-3 bg-divider w-1/3" />
            </div>
          ))
        ) : filtered.length === 0 ? (
          <div className="col-span-full flex items-center justify-center py-24">
            <div className="max-w-sm border border-dashed border-borderLight p-10 text-center">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primaryAccent/10 text-primaryAccent mb-4 mx-auto">
                <Sprout size={28} />
              </div>
              <p className="text-lg font-semibold text-headingText">
                No plants found
              </p>
            </div>
          </div>
        ) : (
          filtered.map((plant) => (
            <div
              key={plant._id}
              className="border border-borderLight bg-surfaceColor p-5 flex flex-col gap-3 hover:shadow-md transition"
            >
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primaryAccent/10 text-primaryAccent flex-shrink-0">
                    <Sprout size={16} />
                  </div>
                  <p className="text-sm font-semibold text-headingText">
                    {plant.plantName}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openEdit(plant)}
                    className="text-primaryAccent hover:opacity-70"
                  >
                    <FiEdit2 size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(plant)}
                    className="text-red-500 hover:text-red-600"
                  >
                    <FiTrash2 size={14} />
                  </button>
                </div>
              </div>

              {plant.gradeMixtures.length > 0 ? (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {plant.gradeMixtures.map((m) => (
                    <span
                      key={m._id || m}
                      className="text-xs px-2 py-0.5 bg-primaryAccent/10 text-primaryAccent border border-primaryAccent/20"
                    >
                      {m.gradeName || "Mixture"}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-descriptionText italic">
                  No mixtures assigned
                </p>
              )}

              <p className="text-xs text-descriptionText pt-1 border-t border-borderLight">
                {plant.gradeMixtures.length} mixture
                {plant.gradeMixtures.length !== 1 ? "s" : ""} assigned
              </p>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      <ModalWrapper
        title={editingPlant ? "Edit Plant" : "Add Plant"}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onPrimaryAction={handleSave}
        primaryActionLabel={saving ? "Saving..." : "Save"}
        primaryLoading={saving}
        maxWidth="max-w-md"
      >
        <div className="space-y-5 text-sm">
          <div>
            <label className="block text-xs text-descriptionText mb-1">
              Plant Name *
            </label>
            <input
              type="text"
              value={form.plantName}
              onChange={(e) => setForm({ ...form, plantName: e.target.value })}
              className="w-full border border-borderLight px-3 py-2 outline-none focus:border-primaryAccent text-sm"
              placeholder="e.g. Plant Alpha"
            />
          </div>

          <div>
            <label className="block text-xs text-descriptionText mb-2">
              Assign Grade Mixtures
            </label>
            {allMixtures.length === 0 ? (
              <p className="text-xs text-descriptionText italic">
                No grade mixtures available. Create them first.
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-2 max-h-52 overflow-y-auto pr-1">
                {allMixtures.map((m) => {
                  const selected = form.gradeMixtures.includes(m._id);
                  return (
                    <button
                      key={m._id}
                      onClick={() => toggleMixture(m._id)}
                      className={`text-left text-xs px-3 py-2 border transition ${
                        selected
                          ? "border-primaryAccent bg-primaryAccent/10 text-primaryAccent"
                          : "border-borderLight text-headingText hover:border-primaryAccent/50"
                      }`}
                    >
                      {m.gradeName}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </ModalWrapper>
    </div>
  );
};

export default PlantsTab;
